// src\components\planning\ManagerCalendarView.jsx
import React, { useState, useRef } from 'react';
import { Calendar, Clock, Plus, ArrowLeft, ArrowRight } from 'lucide-react';

const ManagerCalendarView = ({ 
  resources, 
  batches, 
  onBatchSelect, 
  onBatchMove, 
  onAddBatch,
  selectedBatch
}) => {
  const [currentStartDate, setCurrentStartDate] = useState(new Date('2025-03-31'));
  const calendarRef = useRef(null);
  const [draggingBatch, setDraggingBatch] = useState(null);

  // Générer les dates pour l'affichage (10 jours)
  const dates = Array.from({ length: 10 }, (_, i) => {
    const date = new Date(currentStartDate);
    date.setDate(date.getDate() + i);
    return date;
  });

  // Tranches horaires pour chaque jour
  const timeSlots = [
    { id: 'morning', name: 'Matin', startHour: 6, endHour: 14 },
    { id: 'afternoon', name: 'Après-midi', startHour: 14, endHour: 22 },
    { id: 'night', name: 'Nuit', startHour: 22, endHour: 6 }
  ];

  // Fonction pour formater une date en format français
  const formatDate = (date) => {
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // Fonction pour formater une date en format DD/MM/YYYY
  const formatDateForApi = (date) => {
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Fonction pour convertir le format de date "DD/MM/YYYY" vers un objet Date
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Fonction pour obtenir la couleur du cycle
  const getCycleColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'bg-blue-500';
      case 'A35': return 'bg-purple-500';
      case 'E25': return 'bg-green-500';
      case 'A29': return 'bg-red-500';
      case 'E28': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  // Fonction pour obtenir la couleur de texte du cycle
  const getCycleTextColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'text-blue-700';
      case 'A35': return 'text-purple-700';
      case 'E25': return 'text-green-700';
      case 'A29': return 'text-red-700';
      case 'E28': return 'text-yellow-700';
      default: return 'text-gray-700';
    }
  };

  // Gestionnaire pour naviguer à la période précédente
  const handlePrevPeriod = () => {
    const prevDate = new Date(currentStartDate);
    prevDate.setDate(prevDate.getDate() - 7);
    setCurrentStartDate(prevDate);
  };

  // Gestionnaire pour naviguer à la période suivante
  const handleNextPeriod = () => {
    const nextDate = new Date(currentStartDate);
    nextDate.setDate(nextDate.getDate() + 7);
    setCurrentStartDate(nextDate);
  };

  // Gestionnaire pour le début du glisser-déposer d'une fournée
  const handleDragStart = (e, batch) => {
    setDraggingBatch(batch);
    e.dataTransfer.setData('text/plain', JSON.stringify(batch));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Gestionnaire pour le survol d'une cellule pendant le glisser-déposer
  const handleDragOver = (e, resourceId, date, timeSlot) => {
    e.preventDefault();
    e.currentTarget.classList.add('bg-blue-50');
  };

  // Gestionnaire pour la sortie d'une cellule pendant le glisser-déposer
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-blue-50');
  };

  // Gestionnaire pour le dépôt d'une fournée
  const handleDrop = (e, resourceId, date, timeSlot) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-blue-50');
    
    if (!draggingBatch) return;
    
    // Calculer les nouvelles heures de début et de fin
    let startTime, endTime;
    
    // Déterminer l'heure de début basée sur le créneau
    switch(timeSlot.id) {
      case 'morning':
        startTime = '08:00';
        endTime = '13:00';
        break;
      case 'afternoon':
        startTime = '14:00';
        endTime = '19:00';
        break;
      case 'night':
        startTime = '22:00';
        endTime = '03:00';
        break;
      default:
        startTime = '08:00';
        endTime = '13:00';
    }
    
    // Formater la date pour l'API
    const formattedDate = formatDateForApi(date);
    
    // Appeler la fonction de déplacement du parent
    onBatchMove(draggingBatch.batchNumber, resourceId, formattedDate, startTime, endTime);
    
    setDraggingBatch(null);
  };

  // Fonction pour vérifier si une fournée est dans un créneau particulier
  const isBatchInTimeSlot = (batch, resourceId, date, timeSlot) => {
    // Convertir la date de la fournée en objet Date
    const batchDate = parseDate(batch.date);
    if (!batchDate) return false;
    
    // Vérifier si la ressource correspond
    if (batch.resourceId !== resourceId) return false;
    
    // Vérifier si la date correspond (même jour)
    if (batchDate.getDate() !== date.getDate() || 
        batchDate.getMonth() !== date.getMonth() || 
        batchDate.getFullYear() !== date.getFullYear()) {
      return false;
    }
    
    // Extraire les heures de début et de fin
    const [startHour] = batch.startTime.split(':').map(Number);
    
    // Vérifier si l'heure tombe dans le créneau
    if (timeSlot.id === 'morning' && startHour >= 6 && startHour < 14) {
      return true;
    } else if (timeSlot.id === 'afternoon' && startHour >= 14 && startHour < 22) {
      return true;
    } else if (timeSlot.id === 'night' && (startHour >= 22 || startHour < 6)) {
      return true;
    }
    
    return false;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Navigation entre les périodes */}
      <div className="flex justify-between items-center p-2 border-b bg-gray-50">
        <button 
          className="p-1 rounded hover:bg-gray-200"
          onClick={handlePrevPeriod}
        >
          <ArrowLeft size={16} />
        </button>
        
        <div className="font-medium text-sm">
          {formatDate(dates[0])} - {formatDate(dates[dates.length - 1])}
        </div>
        
        <button 
          className="p-1 rounded hover:bg-gray-200"
          onClick={handleNextPeriod}
        >
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Tableau transposé - Ressources en colonnes, temps en lignes */}
      <div className="flex-grow overflow-auto">
        <table className="min-w-full border-collapse table-fixed">
          <thead>
            <tr>
              <th className="w-20 p-2 border bg-gray-50">Date</th>
              <th className="w-16 p-2 border bg-gray-50">Créneau</th>
              {resources.map(resource => (
                <th 
                  key={resource.id} 
                  className={`p-2 border bg-gray-50 text-center w-32 
                    ${resource.status === 'available' ? 'text-green-600' : 'text-red-600'}`}
                >
                  {resource.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((date, dateIndex) => (
              // Pour chaque jour, trois créneaux (matin, après-midi, nuit)
              timeSlots.map((timeSlot, slotIndex) => (
                <tr key={`${dateIndex}-${slotIndex}`} className={dateIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {/* Date (fusionnée pour les 3 créneaux) */}
                  {slotIndex === 0 && (
                    <td rowSpan={3} className="p-2 border font-medium text-center">
                      {formatDate(date)}
                    </td>
                  )}
                  
                  {/* Créneau horaire */}
                  <td className="p-2 border text-xs">
                    {timeSlot.name}
                  </td>
                  
                  {/* Cellules pour chaque ressource */}
                  {resources.map(resource => {
                    // Filtrer les fournées pour cette cellule
                    const cellBatches = batches.filter(batch => 
                      isBatchInTimeSlot(batch, resource.id, date, timeSlot)
                    );
                    
                    return (
                      <td 
                        key={`${dateIndex}-${slotIndex}-${resource.id}`} 
                        className={`p-1 border relative min-h-16 ${
                          resource.status === 'available' ? 'hover:bg-blue-50 cursor-pointer' : 'bg-gray-100'
                        }`}
                        onClick={() => {
                          if (resource.status === 'available') {
                            // Déterminer les heures selon le créneau
                            let startTime, endTime;
                            switch(timeSlot.id) {
                              case 'morning':
                                startTime = '08:00';
                                endTime = '13:00';
                                break;
                              case 'afternoon':
                                startTime = '14:00';
                                endTime = '19:00';
                                break;
                              case 'night':
                                startTime = '22:00';
                                endTime = '03:00';
                                break;
                              default:
                                startTime = '08:00';
                                endTime = '13:00';
                            }
                            
                            onAddBatch(resource.id, formatDateForApi(date), startTime, endTime);
                          }
                        }}
                        onDragOver={(e) => resource.status === 'available' && handleDragOver(e, resource.id, date, timeSlot)}
                        onDragLeave={(e) => resource.status === 'available' && handleDragLeave(e)}
                        onDrop={(e) => resource.status === 'available' && handleDrop(e, resource.id, date, timeSlot)}
                      >
                        {cellBatches.length > 0 ? (
                          cellBatches.map(batch => (
                            <div
                              key={batch.batchNumber}
                              className={`rounded-md ${getCycleColor(batch.cycleName)} bg-opacity-70 p-1 mb-1 last:mb-0 border-l-2 ${getCycleColor(batch.cycleName)}
                                ${selectedBatch?.batchNumber === batch.batchNumber ? 'ring-2 ring-blue-700' : ''}
                                ${resource.status === 'available' ? 'cursor-move' : ''} text-xs`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onBatchSelect(batch);
                              }}
                              draggable={resource.status === 'available'}
                              onDragStart={(e) => resource.status === 'available' && handleDragStart(e, batch)}
                            >
                              <div className="font-bold truncate">{batch.batchNumber}</div>
                              <div className="flex justify-between items-center">
                                <div className={`font-medium ${getCycleTextColor(batch.cycleName)} truncate`}>{batch.cycleName}</div>
                                <div className="text-gray-600 text-xs">{batch.startTime}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          resource.status === 'available' && (
                            <div className="flex items-center justify-center h-10 opacity-30 hover:opacity-100">
                              <Plus size={16} className="text-blue-500" />
                            </div>
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManagerCalendarView;